// Globals
import React, { ReactNode, useCallback, MouseEvent } from 'react';
import { SelectProps } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';

// Components
import { CloseFullCircleIcon } from '@/icons';
import EmptyDataThumbnail from '../EmptyDataThumbnail/EmptyDataThumbnail';
import PureIconButton from '../PureIconButton/PureIconButton';
import {
  StyledDownIcon,
  StyledDownWrapper,
  StyledPrefix,
  StyledSelect,
  StyledTag,
  StyledWrapper,
} from './SelectStyles';

export type TSelectOption<V> = {
  value: V;
  label: string | JSX.Element;
  disabled?: boolean;
};

export type TSelectOptions<V> = Array<TSelectOption<V>>;

export interface ISelectProps<Value, OptionValue> extends SelectProps<Value, TSelectOption<OptionValue>> {
  options?: TSelectOptions<OptionValue>;
  isMultiselect?: boolean;
  isTags?: boolean;
  noResultMessage?: string;
  prefix?: ReactNode;
}

const Select = <Value, OptionValue>({
  isMultiselect,
  isTags,
  options,
  noResultMessage,
  showArrow = true,
  value,
  suffixIcon,
  prefix,
  id,
  ...props
}: ISelectProps<Value, OptionValue>): JSX.Element => {
  const renderTag = useCallback(({ label, onClose, isMaxTag }: CustomTagProps) => {
    const handleClose = (evt: MouseEvent) => {
      evt.preventDefault();
      evt.stopPropagation();
      onClose();
    };

    const onPreventMouseDown = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <StyledTag onMouseDown={onPreventMouseDown}>
        <span>{label}</span>
        {!isMaxTag && (
          <PureIconButton onClick={handleClose}>
            <CloseFullCircleIcon />
          </PureIconButton>
        )}
      </StyledTag>
    );
  }, []);

  const renderDropdown = useCallback((menu: ReactNode) => <StyledDownWrapper>{menu}</StyledDownWrapper>, []);
  const maxTagPlaceholder = useCallback(() => <span>...</span>, []);

  const selectComponent = (
    <StyledSelect<Value, TSelectOption<OptionValue>>
      {...props}
      id={id}
      value={value}
      options={options}
      optionFilterProp="label"
      tagRender={renderTag}
      dropdownRender={renderDropdown}
      notFoundContent={<EmptyDataThumbnail message={noResultMessage} />}
      {...(isTags ? { mode: 'tags', maxTagPlaceholder } : isMultiselect ? { mode: 'multiple', maxTagPlaceholder } : {})}
      {...(showArrow
        ? {
            suffixIcon: <StyledDownIcon />,
            showArrow: true,
          }
        : { showArrow: false })}
      {...(suffixIcon ? { suffixIcon, showArrow: true } : {})}
      // Hack for disabling browser autocomplete
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      autoComplete="dontshow"
    />
  );

  return (
    <StyledWrapper>
      {!!prefix && <StyledPrefix>{prefix}</StyledPrefix>}

      {selectComponent}
    </StyledWrapper>
  );
};

export default Select;
